import { useEffect } from "react"
import type { RefObject } from "react"

export interface PlayerKeyBindings {
  onPrev?: () => void
  onNext?: () => void
  onToggleMute?: () => void
  onClose?: () => void
  /**
   * Fired on Enter when the keystroke isn't already activating a native
   * control (button/link) — the keyboard sibling of a surface's primary
   * tap affordance (e.g. opening a jacket face from container focus).
   */
  onActivate?: () => void
}

/**
 * Scoped keyboard handler for a player surface. Attaches to the element
 * rather than `window` so arrow keys never steal focus from inputs elsewhere
 * in the page (the previous global capture-phase listener was a footgun — it
 * would hijack arrow keys the moment a video started playing anywhere in the
 * document, including while the user was typing in an unrelated textarea).
 *
 * The element must be focusable — give it `tabIndex={0}` and autofocus if the
 * surface expects immediate keyboard control.
 */
export function usePlayerKeyboard(
  containerRef: RefObject<HTMLElement | null>,
  bindings: PlayerKeyBindings,
): void {
  const { onPrev, onNext, onToggleMute, onClose, onActivate } = bindings

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const handler = (event: KeyboardEvent) => {
      // If the keystroke originated from an editable descendant (form input,
      // contenteditable) or a region that owns its own keys (e.g. an article
      // reader's scroll area), stay out of the way.
      const target = event.target as HTMLElement | null
      if (target && isEditable(target)) return
      if (target?.closest("[data-player-keys-ignore]")) return

      switch (event.key) {
        case "ArrowUp":
        case "k":
          if (onPrev) {
            event.preventDefault()
            onPrev()
          }
          break
        case "ArrowDown":
        case "j":
          if (onNext) {
            event.preventDefault()
            onNext()
          }
          break
        case "m":
          onToggleMute?.()
          break
        case "Escape":
          onClose?.()
          break
        case "Enter":
          // Native activation (focused button/link) wins — no double-fire.
          if (target?.closest("button, a, [role='button']")) return
          if (onActivate) {
            event.preventDefault()
            onActivate()
          }
          break
      }
    }

    el.addEventListener("keydown", handler)
    return () => el.removeEventListener("keydown", handler)
  }, [containerRef, onPrev, onNext, onToggleMute, onClose, onActivate])
}

function isEditable(el: HTMLElement): boolean {
  const tag = el.tagName
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true
  if (el.isContentEditable) return true
  return false
}
